import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useNoteStyles } from './NoteNode.styles';
import { memo, useMemo } from 'react';
import { tokens } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';

const YOUTUBE_RE = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

/**
 * Pre-process content to preserve multiple blank lines.
 * Standard Markdown collapses multiple newlines into a single paragraph break.
 * This converts extra blank lines into <br> tags to preserve visual spacing.
 */
function preserveBlankLines(text: string): string {
  // Replace sequences of 3+ newlines with paragraph break + <br> tags
  // Each extra newline beyond 2 becomes a <br>
  return text.replace(/\n{3,}/g, (match) => {
    const extraLines = match.length - 2; // 2 newlines = normal paragraph break
    return '\n\n' + '<br>\n'.repeat(extraLines);
  });
}

interface NoteMarkdownRendererProps {
  content: string;
  isDarkMode?: boolean;
}

const NoteMarkdownRendererInner = ({ content, isDarkMode = false }: NoteMarkdownRendererProps) => {
  const styles = useNoteStyles();
  const textColor = isDarkMode ? tokens.colorNeutralForeground1 : ottoTokens.noteForeground;

  // Pre-process to preserve multiple blank lines
  const processedContent = useMemo(() => preserveBlankLines(content), [content]);

  if (!content) {
    return (
      <span className={styles.placeholder} style={{ color: isDarkMode ? tokens.colorNeutralForeground4 : undefined }}>
        This is a note. You can use <strong>Markdown</strong> to format the text.
      </span>
    );
  }

  const markdownRenderers: Components = {
    // Explicit br handling for remark-breaks and rehype-raw
    br: () => <br />,
    img: ({ src, alt }) => <img className={styles.markdownImage} src={src} alt={alt ?? ''} />,
    h1: ({ children }) => <h1 className={styles.markdownHeading} style={{ color: textColor }}>{children}</h1>,
    h2: ({ children }) => <h2 className={styles.markdownHeading} style={{ color: textColor }}>{children}</h2>,
    h3: ({ children }) => <h3 className={styles.markdownHeading} style={{ color: textColor }}>{children}</h3>,
    h4: ({ children }) => <h4 className={styles.markdownHeading} style={{ color: textColor }}>{children}</h4>,
    h5: ({ children }) => <h5 className={styles.markdownHeading} style={{ color: textColor }}>{children}</h5>,
    h6: ({ children }) => <h6 className={styles.markdownHeading} style={{ color: textColor }}>{children}</h6>,
    ul: ({ children }) => <ul className={styles.markdownList} style={{ color: textColor }}>{children}</ul>,
    ol: ({ children }) => <ol className={styles.markdownList} style={{ color: textColor }}>{children}</ol>,
    p: ({ children }) => {
      // If the text content is just a YouTube link, render as an embed
      const childArray = Array.isArray(children) ? children : [children];
      if (
        childArray.length === 1 &&
        typeof childArray[0] === 'string'
      ) {
        const match = (childArray[0] as string).match(YOUTUBE_RE);
        if (match?.[1]) {
          return <YoutubeEmbed videoId={match[1]} />;
        }
      }
      return <p className={styles.markdownParagraph} style={{ color: textColor }}>{children}</p>;
    },
    a: ({ href, children }) => {
      // Detect YouTube URLs in links and render as embeds
      if (href) {
        const match = href.match(YOUTUBE_RE);
        if (match?.[1]) {
          return <YoutubeEmbed videoId={match[1]} />;
        }
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: isDarkMode ? tokens.colorBrandForegroundLink : undefined }}>{children}</a>;
    },
  };

  return (
    <ReactMarkdown
      components={markdownRenderers}
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw]}
    >
      {processedContent}
    </ReactMarkdown>
  );
};

export const NoteMarkdownRenderer = memo(NoteMarkdownRendererInner);

const YoutubeEmbed = ({ videoId }: { videoId: string }) => {
  const styles = useNoteStyles();

  const url = useMemo(() => {
    const _url = new URL(`https://www.youtube.com/embed/${videoId}`);
    _url.searchParams.set('enablejsapi', '1');
    return _url.toString();
  }, [videoId]);

  return (
    <iframe
      title="YouTube video player"
      className={styles.youtubeEmbed}
      src={url}
      referrerPolicy="no-referrer"
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
};
